import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Users, Bell, Calendar, GitBranch, Heart, ArrowRight, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { displayWithFallback, formatRelativeTime, getInitials } from '../utils/displayHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Matches() {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('matches'); // 'likes' | 'matches'
  const [matches, setMatches] = useState([]);
  const [likesReceived, setLikesReceived] = useState([]);
  const [likesSent, setLikesSent] = useState([]);
  const [milestones, setMilestones] = useState({});
  const [loading, setLoading] = useState(true);
  const [likingUser, setLikingUser] = useState(null);

  // Theme classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-blue-600';
  const btnSecondary = isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100';
  const tabBg = isDark ? 'bg-slate-800' : 'bg-white';
  const tabActiveBg = isDark ? 'bg-blue-600' : 'bg-blue-500';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchMatches(),
        fetchMilestones(),
        fetchLikesReceived(),
        fetchLikesSent()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API}/milestones`);
      const milestoneMap = {};
      response.data.forEach(m => {
        milestoneMap[m.match_id] = m;
      });
      setMilestones(milestoneMap);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  };

  const fetchLikesReceived = async () => {
    try {
      const response = await axios.get(`${API}/likes/received`);
      setLikesReceived(response.data);
    } catch (error) {
      console.error('Error fetching likes received:', error);
    }
  };

  const fetchLikesSent = async () => {
    try {
      const response = await axios.get(`${API}/likes/sent`);
      setLikesSent(response.data);
    } catch (error) {
      console.error('Error fetching likes sent:', error);
    }
  };

  const handleLikeBack = async (targetUserId, targetName) => {
    setLikingUser(targetUserId);
    try {
      const response = await axios.post(`${API}/discovery/like`, { target_user_id: targetUserId });
      
      if (response.data.matched) {
        toast.success(`You matched with ${targetName}!`);
      } else {
        toast.success(`You liked ${targetName}`);
      }
      
      // Refresh all data
      await fetchData();
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.error('Error liking back:', error);
      toast.error('Failed to like back');
    } finally {
      setLikingUser(null);
    }
  };

  const getMatchPartner = (match) => {
    if (match.user1_id === user?.user_id) {
      return { id: match.user2_id, name: match.user2_name, role: match.user2_role };
    }
    return { id: match.user1_id, name: match.user1_name, role: match.user1_role };
  };

  const getMilestoneStatus = (matchId) => {
    const milestone = milestones[matchId];
    if (!milestone) return null;
    
    const isPendingForMe = milestone.current_state === 'pitch_proposed' && milestone.proposed_by !== user?.user_id;
    const isAwaitingResponse = milestone.current_state === 'pitch_proposed' && milestone.proposed_by === user?.user_id;
    const isScheduled = milestone.current_state === 'pitch_session_scheduled';
    
    return { milestone, isPendingForMe, isAwaitingResponse, isScheduled };
  };

  // Filter pending likes (not yet liked back, not mutual)
  const pendingLikes = likesReceived.filter(like => !like.liked_back && !like.is_mutual);
  const mutualLikesCount = likesReceived.filter(like => like.is_mutual).length;

  if (loading) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className={`text-center ${textSecondary}`}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`} data-testid="matches-page">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-4xl font-bold mb-2 ${textPrimary}`}>Likes & Matches</h1>
          <p className={textSecondary}>Manage your connections and discover new matches</p>
        </div>

        {/* Tab Navigation */}
        <div className={`flex gap-1 p-1 rounded-xl mb-6 ${tabBg} shadow-sm border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <button
            onClick={() => setActiveTab('likes')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'likes'
                ? `${tabActiveBg} text-white shadow-md`
                : `${textSecondary} hover:bg-slate-100 dark:hover:bg-slate-700`
            }`}
            data-testid="tab-likes"
          >
            <Heart size={18} />
            <span>Likes</span>
            {pendingLikes.length > 0 && (
              <Badge className="bg-pink-500 text-white text-xs ml-1">{pendingLikes.length}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('matches')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'matches'
                ? `${tabActiveBg} text-white shadow-md`
                : `${textSecondary} hover:bg-slate-100 dark:hover:bg-slate-700`
            }`}
            data-testid="tab-matches"
          >
            <Users size={18} />
            <span>Matches</span>
            <Badge className={`${isDark ? 'bg-slate-600' : 'bg-slate-200'} ${textMuted} text-xs ml-1`}>{matches.length}</Badge>
          </button>
        </div>

        {/* Likes Tab Content */}
        {activeTab === 'likes' && (
          <div className="space-y-8">
            {/* Likes Received */}
            <div>
              <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}>
                <Heart className="text-pink-500" size={20} />
                Likes Received
                {pendingLikes.length > 0 && (
                  <span className="text-sm font-normal text-pink-500">({pendingLikes.length} pending)</span>
                )}
              </h2>
              
              {likesReceived.length === 0 ? (
                <Card className={`shadow-sm p-8 text-center ${cardBg}`}>
                  <Heart className={`mx-auto mb-3 ${textMuted}`} size={40} />
                  <p className={textSecondary}>No likes received yet</p>
                  <p className={`text-sm ${textMuted}`}>Keep your profile updated to attract more interest!</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {likesReceived.map(like => (
                    <Card 
                      key={like.user_id} 
                      className={`shadow-sm p-4 ${cardBg} ${!like.liked_back && !like.is_mutual ? 'border-l-4 border-l-pink-500' : ''}`}
                      data-testid={`like-received-${like.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {getInitials(like.name)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold ${textPrimary} truncate`}>{displayWithFallback(like.name, 'name')}</h3>
                          <p className={`text-sm ${textMuted} capitalize`}>{displayWithFallback(like.role, 'text')}</p>
                          <p className={`text-xs ${textMuted}`}>{formatRelativeTime(like.liked_at)}</p>
                        </div>
                        <div>
                          {like.is_mutual ? (
                            <Badge className="bg-green-500 text-white">Mutual</Badge>
                          ) : like.liked_back ? (
                            <Badge className="bg-blue-500 text-white">Liked Back</Badge>
                          ) : (
                            <Badge className="bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">Pending</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        {!like.liked_back && !like.is_mutual && (
                          <Button
                            onClick={() => handleLikeBack(like.user_id, like.name)}
                            disabled={likingUser === like.user_id}
                            className="flex-1 bg-pink-500 hover:bg-pink-600 text-white"
                            data-testid={`like-back-${like.user_id}`}
                          >
                            <Heart size={16} className="mr-2" />
                            {likingUser === like.user_id ? 'Liking...' : 'Like Back'}
                          </Button>
                        )}
                        <Link to={`/profile/${like.user_id}`} className={like.liked_back || like.is_mutual ? 'flex-1' : ''}>
                          <Button className={`w-full border ${btnSecondary}`} variant="outline">
                            View Profile
                          </Button>
                        </Link>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Likes Sent */}
            <div>
              <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}>
                <ArrowRight className="text-blue-500" size={20} />
                Likes Sent
                <span className="text-sm font-normal ${textMuted}">({likesSent.length})</span>
              </h2>
              
              {likesSent.length === 0 ? (
                <Card className={`shadow-sm p-8 text-center ${cardBg}`}>
                  <UserPlus className={`mx-auto mb-3 ${textMuted}`} size={40} />
                  <p className={textSecondary}>You haven't liked anyone yet</p>
                  <Link to="/discovery" className="inline-block mt-3">
                    <Button className="btn-primary">Discover Profiles</Button>
                  </Link>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {likesSent.map(like => (
                    <Card 
                      key={like.user_id} 
                      className={`shadow-sm p-4 ${cardBg}`}
                      data-testid={`like-sent-${like.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {getInitials(like.name)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold ${textPrimary} truncate`}>{displayWithFallback(like.name, 'name')}</h3>
                          <p className={`text-sm ${textMuted} capitalize`}>{displayWithFallback(like.role, 'text')}</p>
                          <p className={`text-xs ${textMuted}`}>{formatRelativeTime(like.liked_at)}</p>
                        </div>
                        <div>
                          {like.is_mutual ? (
                            <Badge className="bg-green-500 text-white">Matched!</Badge>
                          ) : like.liked_back ? (
                            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Liked Back</Badge>
                          ) : (
                            <Badge className={`${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>Pending</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        <Link to={`/profile/${like.user_id}`} className="flex-1">
                          <Button className={`w-full border ${btnSecondary}`} variant="outline">
                            View Profile
                          </Button>
                        </Link>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Matches Tab Content */}
        {activeTab === 'matches' && (
          <>
            {matches.length === 0 ? (
              <Card className={`shadow-sm p-12 text-center ${cardBg}`}>
                <Users className={`mx-auto mb-4 ${textSecondary}`} size={64} />
                <h2 className={`text-xl font-bold mb-2 ${textPrimary}`}>No matches yet</h2>
                <p className={`mb-6 ${textSecondary}`}>
                  {pendingLikes.length > 0 
                    ? `You have ${pendingLikes.length} pending like${pendingLikes.length > 1 ? 's' : ''} - like them back to create a match!`
                    : 'Start liking profiles to create matches!'}
                </p>
                {pendingLikes.length > 0 ? (
                  <Button onClick={() => setActiveTab('likes')} className="btn-primary" data-testid="view-likes-button">
                    <Heart size={16} className="mr-2" />
                    View Pending Likes
                  </Button>
                ) : (
                  <Link to="/discovery">
                    <Button className="btn-primary" data-testid="discover-button">Discover Profiles</Button>
                  </Link>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {matches.map(match => {
                  const partner = getMatchPartner(match);
                  const status = getMilestoneStatus(match.match_id);
                  
                  return (
                    <Card key={match.match_id} className={`shadow-sm p-6 ${cardBg} relative`} data-testid={`match-card-${match.match_id}`}>
                      {/* Status Badge */}
                      {status?.isPendingForMe && (
                        <Badge className="absolute -top-2 -right-2 bg-amber-500 text-white animate-pulse" data-testid={`pending-badge-${match.match_id}`}>
                          <Bell size={12} className="mr-1" />
                          Action Required
                        </Badge>
                      )}
                      {status?.isScheduled && (
                        <Badge className="absolute -top-2 -right-2 bg-green-500 text-white" data-testid={`scheduled-badge-${match.match_id}`}>
                          <Calendar size={12} className="mr-1" />
                          Pitch Scheduled
                        </Badge>
                      )}
                      {status?.isAwaitingResponse && (
                        <Badge className="absolute -top-2 -right-2 bg-blue-500 text-white" variant="outline" data-testid={`awaiting-badge-${match.match_id}`}>
                          Awaiting Response
                        </Badge>
                      )}
                      
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center">
                          <span className="text-white font-bold text-xl">
                            {getInitials(partner.name)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${textPrimary}`}>{displayWithFallback(partner.name, 'name')}</h3>
                          <p className={`text-sm capitalize ${textSecondary}`}>{displayWithFallback(partner.role, 'text')}</p>
                        </div>
                      </div>

                      {/* Milestone Status */}
                      {status?.milestone && (
                        <div className={`mb-4 p-2 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                          <div className="flex items-center gap-2">
                            <GitBranch size={14} className={textMuted} />
                            <span className={`text-xs ${textMuted}`}>
                              {status.milestone.current_state === 'matched' && 'Matched'}
                              {status.milestone.current_state === 'initial_conversation' && 'Initial Conversation'}
                              {status.milestone.current_state === 'pitch_proposed' && 'Pitch Proposed'}
                              {status.milestone.current_state === 'pitch_session_scheduled' && 'Pitch Scheduled'}
                              {status.milestone.current_state === 'pitch_session_completed' && 'Pitch Completed'}
                              {status.milestone.current_state === 'due_diligence' && 'Due Diligence'}
                              {status.milestone.current_state === 'investment_decision' && 'Decision Made'}
                            </span>
                          </div>
                          {status.isScheduled && status.milestone.confirmed_pitch_slot && (
                            <p className={`text-xs ${textMuted} mt-1`}>
                              {new Date(status.milestone.confirmed_pitch_slot).toLocaleDateString('en-US', {
                                weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="mb-4">
                        <span className={`text-xs ${textMuted}`}>Matched on</span>
                        <p className={`text-sm ${textPrimary}`}>
                          {new Date(match.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Link to={`/messages/${match.match_id}`} className="flex-1">
                          <Button className="btn-primary w-full" data-testid={`message-${match.match_id}`}>
                            <MessageSquare size={16} className="mr-2" />
                            Message
                          </Button>
                        </Link>
                        <Link to={`/profile/${partner.id}`}>
                          <Button className={`border ${btnSecondary}`} variant="outline" data-testid={`view-profile-${partner.id}`}>
                            {status?.isPendingForMe ? 'Respond' : 'View Profile'}
                          </Button>
                        </Link>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
